import { FlightReportCheck, LogChartsResponse } from '../types';
import { THRESHOLD_COLORS } from '../panels/chartPalette';
import { AgentChart, ModeBand, PlotSpec, SeriesAgentChart, TimeMarker } from './layoutTypes';

/* The two tabs nobody saves: the box's overview of every log, and the model's charts. */

const OVERVIEW_ORDER = [
  'velocity', 'manual_control', 'actuator_motors', 'motor_outputs',
  'esc_rpm', 'battery', 'vibration', 'raw_gyro', 'raw_mag',
  'gps_uncertainty', 'angular_velocity_fft', 'failsafe_flags',
];

const thresholdLabel = (k: string) => (k === 'green' ? 'Good' : k === 'orange' ? 'Warning' : k);
const thresholdColor = (k: string) =>
  k === 'green' ? THRESHOLD_COLORS.good : k === 'orange' ? THRESHOLD_COLORS.warning : THRESHOLD_COLORS.critical;

export function overviewPlots(data: LogChartsResponse | null): PlotSpec[] {
  const charts = data?.charts || {};
  const keys = [
    ...OVERVIEW_ORDER.filter(k => charts[k]),
    ...Object.keys(charts).filter(k => !OVERVIEW_ORDER.includes(k)),
  ];
  return keys.flatMap((key): PlotSpec[] => {
    const c = charts[key];
    const isSpectrum = !c.timestamps_s && !!c.frequencies;
    const x = c.timestamps_s || c.frequencies;
    if (!x || x.length === 0) return [];
    return [{
      id: `overview-${key}`,
      title: c.title,
      series: [],
      inline: {
        t: x,
        series: c.series || {},
        y_label: c.y_label,
        bars: isSpectrum || undefined,
        x_label: isSpectrum ? 'Hz' : undefined,
        reference_lines: c.thresholds
          ? Object.entries(c.thresholds).map(([k, value]) => ({ value, label: thresholdLabel(k), color: thresholdColor(k) }))
          : undefined,
      },
    }];
  });
}

// A guard, not `c.kind === 'series'`: without strictNullChecks the optional `kind`
// on the inline shape does not discriminate, and the else branch collapses to never.
const isSeriesChart = (c: AgentChart): c is SeriesAgentChart => c.kind === 'series';

export function analystPlots(charts: AgentChart[]): PlotSpec[] {
  return charts.flatMap((c, i): PlotSpec[] => {
    const id = `analyst-${i}`;
    if (isSeriesChart(c)) {
      const refs = (c.series || []).map(r => ({ topic: r.topic, instance: r.instance ?? 0, field: r.field }));
      return [{ id, title: c.title, series: refs }];
    }
    if (!Array.isArray(c.timestamps_s)) return [];
    return [{
      id,
      title: c.title,
      series: [],
      inline: {
        t: c.timestamps_s,
        series: c.series || {},
        y_label: c.y_label,
        reference_lines: c.reference_lines,
        highlights: c.highlights,
      },
    }];
  });
}

/* ── Report overlays: flight-mode bands and the moments a check failed or warned ── */

// "19=SHOW" when the branch facts name the mode, a bare "19" when they do not.
const modeName = (raw: unknown): string => {
  const v = String(raw).trim();
  const eq = v.indexOf('=');
  return eq >= 0 ? v.slice(eq + 1) : v;
};

export function buildModeBands(timeline: any[] | undefined, endT: number): ModeBand[] {
  // The report groups its timeline by topic, so sort before reading it as a sequence.
  const rows = (timeline || [])
    .filter(r => r?.topic === 'vehicle_status' && typeof r.t === 'number')
    .sort((a, b) => a.t - b.t);
  const starts: { t: number; label: string }[] = [];
  let truncated = false;
  rows.forEach(r => {
    if (r.note) {
      truncated = true;
    } else if (r.initial?.nav_state != null) {
      starts.push({ t: r.t, label: modeName(r.initial.nav_state) });
    } else if (typeof r.changes?.nav_state === 'string') {
      starts.push({ t: r.t, label: modeName(r.changes.nav_state.split('->').pop()) });
    }
  });
  const bands = starts.map((s, i) => ({
    start_s: s.t,
    end_s: i + 1 < starts.length ? starts[i + 1].t : endT,
    label: s.label,
  }));
  // A capped timeline stops listing changes, so the last listed mode has no known end.
  if (truncated) bands.pop();
  return bands.filter(b => b.end_s > b.start_s);
}

const MARKER_LABEL_MAX = 28;

export function buildMarkers(checks: FlightReportCheck[] | undefined): TimeMarker[] {
  const byMoment = new Map<string, { t: number; names: string[]; fail: boolean }>();
  (checks || []).forEach(c => {
    if (typeof c.t !== 'number' || (c.status !== 'FAIL' && c.status !== 'WARN')) return;
    const key = c.t.toFixed(1);
    const m = byMoment.get(key) || { t: c.t, names: [], fail: false };
    m.names.push(c.name);
    m.fail = m.fail || c.status === 'FAIL';
    byMoment.set(key, m);
  });
  return Array.from(byMoment.values()).map(m => {
    const label = m.names.join(', ');
    return {
      t: m.t,
      label: label.length > MARKER_LABEL_MAX ? `${label.slice(0, MARKER_LABEL_MAX - 1)}…` : label,
      color: m.fail ? THRESHOLD_COLORS.critical : THRESHOLD_COLORS.warning,
    };
  });
}

/** The end of the flight as the overview sees it, for the last mode band. */
export function flightEnd(data: LogChartsResponse | null, fallback = 0): number {
  let end = 0;
  Object.values(data?.charts || {}).forEach(c => {
    const ts = c.timestamps_s;
    if (ts && ts.length) end = Math.max(end, ts[ts.length - 1]);
  });
  return end || data?.info?.duration_s || fallback;
}
